import { useMutationApi, useQueryApi } from 'utils/api'

import type { QueryApiConfig } from 'utils/api'
import type { AxiosError } from 'axios'

const BASE_PATH = `${
  process.env.NEXT_PUBLIC_BCKS_USE_AMBASSADOR === 'true' ? '/guru' : ''
}/guru-ks/v1/ks`

const API_SELECTIONS_KS_PATH = `${BASE_PATH}/selections`

export type SelectionCandidateState =
  | 'notInvited'
  | 'invitation'
  | 'documentSubmission'
  | 'documentVerification'
  | 'documentVerificationFailed'
  | 'adminFinished'
  | 'adminFailed'
  | 'adminCanceled'
  | 'napzaInvitation'
  | 'napzaVerification'
  | 'napzaFinished'
  | 'napzaFailed'
  | 'substanceInProgress'
  | 'substanceFinished'
  | 'substanceFailed'
  | 'diklatPending'
  | 'diklatInProgress'
  | 'diklatFinished'
  | 'diklatFailed'
  | 'diklatCertificateIssued'
  | 'napzaRegularInvitation'
  | 'napzaRegularVerification'
  | 'napzaRegularFinished'
  | 'napzaRegularFailed'
  | 'overrideFinished'
  | 'pairing'
  | 'rejectOffer'
  | 'reject'
  | 'failed'
  | 'finished'
  | 'regularFinished'

export type SelectionState = 'documentVerification' | 'finished' | 'canceled'

export type SelectionType = 'administration' | 'substance' | 'override'

export type SelectionVerificationState =
  | 'NOT_SUBMITTED'
  | 'NOT_STARTED'
  | 'ON_PROGRESS'
  | 'VALID'
  | 'INVALID'

export type SelectionDocument =
  | 'hasilPenilaianKinerja'
  | 'catatanKepolisian'
  | 'pengalamanManajerial'
  | 'bebasHukuman'
  | 'paktaIntegritas'

export const allSelectionDocumentTypes: SelectionDocument[] = [
  'hasilPenilaianKinerja',
  'catatanKepolisian',
  'pengalamanManajerial',
  'bebasHukuman',
  'paktaIntegritas',
]

export type SelectionDocumentState =
  | 'VALID'
  | 'INVALID'
  | 'UNVERIFIED'
  | 'NEED_REVISION'

export interface ResponseSuccessGetKSInvitationDocuments {
  hasilPenilaianKinerjaUrl: string | null
  hasilPenilaianKinerjaThumbnailUrl: string | null
  catatanKepolisianUrl: string | null
  catatanKepolisianThumbnailUrl: string | null
  pengalamanManajerialUrl: string | null
  pengalamanManajerialThumbnailUrl: string | null
  kesehatanUrl: string | null
  kesehatanThumbnailUrl: string | null
  bebasHukumanUrl: string | null
  bebasHukumanThumbnailUrl: string | null
  paktaIntegritasUrl: string | null
  paktaIntegritasThumbnailUrl: string | null
  napzaUrl: string | null
  napzaThumbnailUrl: string | null
}

export interface ResponseSuccessKSSelectionCandidate {
  deadlineDate: string
  endDate: string
  invitationFileUrl: string
  invitationText: string
  selectionCandidateState: SelectionCandidateState
  selectionState: SelectionState
  selectionType: SelectionType
  selectionId: string
  startDate: string
  totalCandidate: string
  schoolName: string
  schoolCity: string
  reason: string
}

export interface ResponseSuccessUploadSubmissionDocument {
  id: string
  fileType: string
}

export interface ResponseErrorUploadSubmissionDocument {
  message: string
}

export interface ResponseSuccessKSSelectionResult {
  isSelected: boolean
  skFileUrl: string
  candidate: {
    nip: string
    name: string
    dateOfBirth: string
  }
  school: {
    name: string
    city: string
  }
}

export interface RequestUploadSubmissionDocument {
  selectionId: string
  fileType:
    | 'HASIL_PENILAIAN_KINERJA'
    | 'SKCK'
    | 'SK_MANAJERIAL'
    | 'SKK'
    | 'SK_BEBAS_HUKUMAN'
  file: File
}

export function useMutateAPIGetKSSelectionCandidate() {
  return useMutationApi<ResponseSuccessKSSelectionCandidate, Error, {}>(
    `${API_SELECTIONS_KS_PATH}/candidate`,
    {
      axiosConfig: {
        method: 'GET',
      },
    }
  )
}

export function useQueryAPIGetKSSelectionCandidate(
  queryConfig: QueryApiConfig<ResponseSuccessKSSelectionCandidate>['queryConfig'] = {}
) {
  return useQueryApi<ResponseSuccessKSSelectionCandidate>(
    `${API_SELECTIONS_KS_PATH}/candidate`,
    {
      queryConfig: {
        useErrorBoundary: false,
        cacheTime: 0,
        ...queryConfig,
      },
    }
  )
}

interface ParamsGetKSActiveSelection {
  selectionType:
    | 'KEPSEK'
    | 'NON_REGULAR'
    | 'ADMINISTRATION'
    | 'SUBSTANCE'
    | 'DIKLAT'
    | 'NON_ASN'
    | 'DIKLAT_ELIGIBLE'
    | 'NAPZA_SUBMISSION'
    | 'NAPZA_REGULAR_SUBMISSION'
    | 'TERMINATION'
}
interface ResponseGetKSActiveSelection {
  selectionId: string
  startDate: string
  endDate: string
}

export function useQueryAPIGetActiveSelection(
  params: ParamsGetKSActiveSelection,
  queryConfig: QueryApiConfig<ResponseGetKSActiveSelection>['queryConfig'] = {}
) {
  return useQueryApi<ResponseGetKSActiveSelection>(
    `${BASE_PATH}/candidates/selections/active`,
    {
      axiosConfig: {
        params,
      },
      queryConfig: {
        useErrorBoundary: false,
        cacheTime: 0,
        ...queryConfig,
      },
    }
  )
}

export function useMutateAPIAcceptKSInvitation(selectionId: string) {
  return useMutationApi<{ success: boolean }, Error, {}>(
    `${API_SELECTIONS_KS_PATH}/${selectionId}/invitations`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )
}

export function useMutateAPIUploadSubmission(selectionId: string) {
  return useMutationApi<
    ResponseSuccessUploadSubmissionDocument,
    AxiosError<ResponseErrorUploadSubmissionDocument>,
    FormData
  >(`${API_SELECTIONS_KS_PATH}/${selectionId}/submissions`, {
    axiosConfig: {
      method: 'POST',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  })
}

export function useQueryAPIGetKSInvitationDocuments(selectionId: string) {
  return useQueryApi<ResponseSuccessGetKSInvitationDocuments>(
    `${API_SELECTIONS_KS_PATH}/${selectionId}/submissions`,
    {
      queryConfig: {
        useErrorBoundary: false,
        enabled: !!selectionId,
      },
    }
  )
}

export function useMutateAPIDeleteKSInvitationDocuments() {
  return useMutationApi<{ success: boolean }, Error, {}>(
    (data) =>
      `${API_SELECTIONS_KS_PATH}/${data.selectionId}/submissions?fileType=${data.fileType}`,
    {
      axiosConfig: {
        method: 'DELETE',
      },
    }
  )
}

export function useMutateAPIFinalizeSubmission(selectionId: string) {
  return useMutationApi<{ success: boolean }, Error, {}>(
    `${API_SELECTIONS_KS_PATH}/${selectionId}/submissions/finalized`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )
}

export function useQueryApiGetKSSelectionResult() {
  return useQueryApi<ResponseSuccessKSSelectionResult>(
    `${API_SELECTIONS_KS_PATH}/result`,
    {
      queryConfig: {
        useErrorBoundary: false,
      },
    }
  )
}

export type ResponseSuccessGetVerificationDocuments = {
  [key in SelectionDocument]: {
    url: string
    thumbnailUrl: string
    state: SelectionDocumentState
    reason: string
  }
} & {
  id: string
  state: SelectionVerificationState
}

export function useQueryAPIGetVerificationDocuments(
  selectionId: string,
  queryConfig: QueryApiConfig<ResponseSuccessGetVerificationDocuments>['queryConfig'] = {}
) {
  return useQueryApi<ResponseSuccessGetVerificationDocuments>(
    `${API_SELECTIONS_KS_PATH}/${selectionId}/verifications/me`,
    {
      queryConfig: {
        useErrorBoundary: false,
        enabled: !!selectionId,
        ...queryConfig,
      },
    }
  )
}

export function useMutateAPIKSSelfProposal(selectionId: string) {
  return useMutationApi<{ success: boolean }, Error, {}>(
    `${API_SELECTIONS_KS_PATH}/${selectionId}/invitations/self-proposal`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )
}

export const API_PATH_GET_KS_SELECTION_PROPOSAL = `${API_SELECTIONS_KS_PATH}/ks-proposal`
export function useQueryAPIGetKSSelectionProposal(
  queryConfig: QueryApiConfig<ResponseGetKSSelectionProposal>['queryConfig'] = {}
) {
  return useQueryApi<ResponseGetKSSelectionProposal>(
    API_PATH_GET_KS_SELECTION_PROPOSAL,
    {
      queryConfig,
    }
  )
}
export interface ResponseGetKSSelectionProposal {
  selectionId: string
  proposalStatus: ProposalStatusType
  candidates: Array<CandidateItem>
  selectionStartDate: string
  selectionEndDate: string
}
export type ProposalStatusType = 'selected' | 'not_selected'
export type CandidateItem = {
  candidateId: string
  name: string
  nip: string
}

export function useMutateAPIKSProposal(selectionId: string) {
  return useMutationApi<unknown, Error, { candidateIds: Array<string> }>(
    `${API_SELECTIONS_KS_PATH}/${selectionId}/invitations/ks-proposal`,
    {
      axiosConfig: {
        method: 'POST',
      },
    }
  )
}

interface ParamsGetLMSCertificate {
  selectionId: string
  candidateId: string
}

interface ResponseGetLMSCertificate {
  fileUrl: string
}

export function useQueryApiGetLMSCertificate(
  { selectionId, candidateId }: ParamsGetLMSCertificate,
  queryConfig: QueryApiConfig<ResponseGetLMSCertificate>['queryConfig'] = {}
) {
  return useQueryApi<ResponseGetLMSCertificate>(
    `${API_SELECTIONS_KS_PATH}/${selectionId}/candidates/${candidateId}/lms-certificate`,
    {
      queryConfig: {
        useErrorBoundary: false,
        enabled: !!selectionId && !!candidateId,
        ...queryConfig,
      },
    }
  )
}
